import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { stocksApi, brokerApi, watchlistApi } from './api';
import Sidebar from './Sidebar';
import StockHeader from './StockHeader';
import MetricCard from './MetricCard';
import TickerChartCard from './TickerChartCard';
import AIAnalystNote from './AIAnalystNote';
import NewsCard from './NewsCard';
import PortfolioImpactCard from './PortfolioImpactCard';
import TradeActionPanel from './TradeActionPanel';
import StockLink from './StockLink';

// A small curated peer list, not an algorithmic recommendation — same spirit as the
// MOVERS_UNIVERSE constant on the backend. Falls back to broad index ETFs for unknown sectors.
const SECTOR_PEERS = {
  AAPL: ['MSFT', 'GOOGL', 'NVDA', 'QQQ', 'SPY'],
  MSFT: ['AAPL', 'GOOGL', 'AMZN', 'QQQ', 'SPY'],
  GOOGL: ['MSFT', 'META', 'AMZN', 'QQQ', 'SPY'],
  AMZN: ['MSFT', 'GOOGL', 'WMT', 'QQQ', 'SPY'],
  NVDA: ['AMD', 'AVGO', 'MSFT', 'QQQ', 'SPY'],
  TSLA: ['F', 'GM', 'NVDA', 'QQQ', 'SPY'],
  META: ['GOOGL', 'AMZN', 'MSFT', 'QQQ', 'SPY'],
};
const DEFAULT_PEERS = ['SPY', 'QQQ', 'DIA', 'IWM'];

function fmtCompact(n) {
  if (n == null) return undefined;
  const num = Number(n);
  if (Number.isNaN(num)) return undefined;
  if (Math.abs(num) >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  return `$${num.toLocaleString()}`;
}

function fmtPct(n) {
  return n == null ? undefined : `${(Number(n) * 100).toFixed(2)}%`;
}

export default function StockAnalyzerPage() {
  const { symbol: rawSymbol } = useParams();
  const symbol = (rawSymbol || '').toUpperCase();

  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [news, setNews] = useState(null);
  const [newsError, setNewsError] = useState(null);

  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const [brokerStatus, setBrokerStatus] = useState(null);
  const [positions, setPositions] = useState(null);
  const [account, setAccount] = useState(null);
  const [watchlist, setWatchlist] = useState(null);
  const [watchlistBusy, setWatchlistBusy] = useState(false);

  useEffect(() => {
    setQuote(null);
    setQuoteError(null);
    setProfile(null);
    setMetrics(null);
    setNews(null);
    setAnalysis(null);

    stocksApi.quote(symbol).then(setQuote).catch((err) => setQuoteError(err.message));
    stocksApi.profile(symbol).then(setProfile).catch(() => {});
    stocksApi.metrics(symbol).then(setMetrics).catch(() => {});
    stocksApi.news(symbol).then(setNews).catch((err) => setNewsError(err.message));
  }, [symbol]);

  useEffect(() => {
    brokerApi.status().then(setBrokerStatus).catch(() => setBrokerStatus({ connected: false }));
    brokerApi.positions().then(setPositions).catch(() => setPositions([]));
    brokerApi.account().then(setAccount).catch(() => {});
    watchlistApi.list().then(setWatchlist).catch(() => setWatchlist([]));
  }, []);

  useEffect(() => {
    if (!quote || quote.error || news === null) return;
    const position = positions?.find((p) => p.symbol === symbol);
    setAnalysisLoading(true);
    setAnalysisError(null);
    stocksApi
      .analysis(symbol, { quote, news, position: position || null })
      .then(setAnalysis)
      .catch((err) => setAnalysisError(err.message))
      .finally(() => setAnalysisLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, news, symbol]);

  const position = positions?.find((p) => p.symbol === symbol);
  const inWatchlist = watchlist?.includes(symbol);
  const peers = SECTOR_PEERS[symbol] || DEFAULT_PEERS;

  const newsInsightFor = (headline) => analysis?.newsInsights?.find((n) => n.headline === headline);

  const toggleWatchlist = async () => {
    setWatchlistBusy(true);
    try {
      const updated = inWatchlist ? await watchlistApi.remove(symbol) : await watchlistApi.add(symbol);
      setWatchlist(updated);
    } finally {
      setWatchlistBusy(false);
    }
  };

  const metricCards = useMemo(
    () => [
      { label: 'Price', value: quote?.price != null ? `$${quote.price.toFixed(2)}` : undefined },
      { label: 'Day Range', value: quote?.dayLow != null && quote?.dayHigh != null ? `$${quote.dayLow.toFixed(2)} – $${quote.dayHigh.toFixed(2)}` : undefined },
      { label: '52 Week Range', value: quote?.yearLow != null && quote?.yearHigh != null ? `$${quote.yearLow.toFixed(2)} – $${quote.yearHigh.toFixed(2)}` : undefined },
      { label: 'Volume', value: quote?.volume?.toLocaleString() },
      { label: 'Avg Volume', value: quote?.avgVolume?.toLocaleString() },
      { label: 'Market Cap', value: fmtCompact(quote?.marketCap) },
      { label: 'P/E Ratio', value: quote?.peRatio != null ? quote.peRatio.toFixed(2) : undefined },
      { label: 'Previous Close', value: quote?.previousClose != null ? `$${quote.previousClose.toFixed(2)}` : undefined },
      { label: 'Open', value: quote?.open != null ? `$${quote.open.toFixed(2)}` : undefined },
    ],
    [quote]
  );

  const keyMetricCards = useMemo(
    () => [
      { label: 'Market Cap', value: fmtCompact(metrics?.marketCap) },
      { label: 'P/E Ratio', value: metrics?.peRatio != null ? metrics.peRatio.toFixed(2) : undefined },
      { label: 'EPS', value: metrics?.eps != null ? metrics.eps.toFixed(2) : undefined },
      { label: 'Dividend Yield', value: fmtPct(metrics?.dividendYield) },
      { label: '52 Week High', value: metrics?.fiftyTwoWeekHigh != null ? `$${metrics.fiftyTwoWeekHigh.toFixed(2)}` : undefined },
      { label: '52 Week Low', value: metrics?.fiftyTwoWeekLow != null ? `$${metrics.fiftyTwoWeekLow.toFixed(2)}` : undefined },
      { label: 'Beta', value: metrics?.beta != null ? metrics.beta.toFixed(2) : undefined },
      { label: 'Avg Volume', value: metrics?.avgVolume?.toLocaleString() },
      { label: 'Revenue', value: fmtCompact(metrics?.revenue) },
      { label: 'Profit Margin', value: fmtPct(metrics?.profitMargin) },
    ],
    [metrics]
  );

  return (
    <div className="quantumtrade-app">
      <Sidebar activeTab={null} onTabChange={() => {}} brokerStatus={brokerStatus} />

      <main className="app-main">
        <nav className="breadcrumb">
          <Link to="/">Dashboard</Link>
          <span> / </span>
          <span>Stocks</span>
          <span> / </span>
          <span>{symbol}</span>
        </nav>
        <Link to="/" className="back-link">
          ← Back to Dashboard
        </Link>

        {quoteError ? (
          <div className="error-box" style={{ marginTop: 16 }}>
            Could not load {symbol}: {quoteError}
          </div>
        ) : (
          <div className="stock-analyzer">
            <StockHeader symbol={symbol} quote={quote} profile={profile} />

            <TradeActionPanel
              symbol={symbol}
              brokerStatus={brokerStatus}
              inWatchlist={inWatchlist}
              onToggleWatchlist={toggleWatchlist}
              watchlistBusy={watchlistBusy}
            />

            <div className="stats-grid">
              {metricCards.map((m) => (
                <MetricCard key={m.label} label={m.label} value={m.value} />
              ))}
            </div>

            <TickerChartCard symbol={symbol} tall />
            <p className="input-hint">Past performance does not guarantee future results.</p>

            <AIAnalystNote analysis={analysis} loading={analysisLoading} error={analysisError} />

            <section className="dashboard-section">
              <h3>News</h3>
              {newsError ? (
                <div className="error-box">{newsError}</div>
              ) : news === null ? (
                <div className="skeleton-list">
                  <span className="skeleton-bar" />
                  <span className="skeleton-bar" />
                </div>
              ) : news.length === 0 ? (
                <div className="empty-state">
                  <p>No recent news found for this symbol.</p>
                  <small>Try again later or research another ticker.</small>
                </div>
              ) : (
                <div className="news-list">
                  {news.map((article) => (
                    <NewsCard key={article.url || article.headline} article={article} insight={newsInsightFor(article.headline)} />
                  ))}
                </div>
              )}
            </section>

            <section className="dashboard-section">
              <h3>Key Metrics</h3>
              <div className="stats-grid">
                {keyMetricCards.map((m) => (
                  <MetricCard key={m.label} label={m.label} value={m.value} />
                ))}
              </div>
            </section>

            <PortfolioImpactCard
              position={position}
              portfolioValue={account ? Number(account.portfolio_value) : null}
              portfolioImpact={analysis?.portfolioImpact}
            />

            <section className="dashboard-section">
              <h3>Related</h3>
              <div className="related-tickers">
                {peers.map((p) => (
                  <StockLink key={p} symbol={p} className="related-ticker-pill" />
                ))}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
